import { isFunction, isObject } from 'lodash-es'

import { ErrorBoundary } from 'tools/Uniform'
import { freeze } from 'utils/dom'
import { joinPath } from 'utils/string'

import { authZ } from 'app/Auth/lib'

/*******************************************************************************


see README.md


*******************************************************************************/
export function normalizeNavPoint(list, point, parent) {
  let {
    id,
    title,
    info,
    type,
    nav,
    tab, // used to override the name in a tab, rather than the title
    subTab, // used to override the name in a sub Tab
    // view,
    route = {},
    children = [],
    pathTo,
    ...leftover
  } = point

  if (Object.keys(leftover).length > 0) {
    console.warn('leftover', leftover)
    throw new Error(
      `Mis-configured nav point: leftover object keys when normalizing: ${id}`
    )
  }

  id = id.toLowerCase()
  route = normalizeRoute(route, point)
  title = asFunction(title)
  tab = tab ? asFunction(tab) : title
  subTab = subTab ? asFunction(subTab) : title

  pathTo = normalizePathTo(pathTo, route.base, id, parent)

  let obj = {
    id,
    type,
    title,
    route,
    tab,
    subTab,
    pathTo,
    nav: undefined,
    info,
    parentId: parent?.id
  }

  if (nav) obj.nav = normalizeNav(nav, obj)

  const result = children.reduce(normalizeChild, { parent: obj, list: [] })
  obj.children = result.list

  return list.concat(freeze(obj))
}

////////////////////////////////////////////////////////////////////////////////
function normalizePathTo(innerPathTo, base, id, parent) {
  // more involved to get less function calls stacked
  if (innerPathTo) {
    if (parent?.pathTo) {
      const parentPathTo = parent.pathTo
      return (args = {}) =>
        innerPathTo({
          ...args,
          base,
          parent: parentPathTo,
          root: parentPathTo(args)
        })
    } else {
      return (args = {}) => innerPathTo({ ...args, base })
    }
  }
  if (parent?.pathTo) {
    const parentPathTo = parent.pathTo
    return (args = {}) => joinPath(parentPathTo(args), base)
  } else {
    return (args = {}) => joinPath(args.root, base)
  }
}

////////////////////////////////////////////////////////////////////////////////
function asFunction(data) {
  if (data === undefined) return () => ''
  if (typeof data !== 'function') {
    return () => data
  }
  return data
}

////////////////////////////////////////////////////////////////////////////////
function normalizeNav(
  {
    visible,
    name,
    tabHead,
    tabAdd,
    menuHead,
    menuAdd,
    styled,
    fg,
    color,
    icon,
    uses,
    divName,
    layout,
    ...leftover
  },
  parent
) {
  if (Object.keys(leftover).length > 0) {
    console.warn('leftover', leftover)
    throw new Error(
      `Mis-configured point.nav: leftover object keys when normalizing: ${parent.id}`
    )
  }

  // if undefined we assume it is visible always
  if (visible === false) {
    visible = () => false
  } else if (!visible) {
    visible = undefined
  } else {
    if (isFunction(visible)) {
      // do nothing
    } else if (isObject(visible)) {
      visible = genAuthFunc(visible)
    } else {
      throw new Error('visible nav value is neither a function nor an object')
    }
  }

  return freeze({
    id: parent.id,
    visible,
    uses,
    name: asFunction(name || parent.title),
    divName: asFunction(divName || name || parent.title),
    tabHead,
    tabAdd,
    menuHead,
    menuAdd,
    color,
    styled,
    fg,
    icon,
    layout
  })
}

function genAuthFunc({ identified, ...auth }) {
  const hasAuthZ = Object.keys(auth).length > 0

  if (identified && hasAuthZ) {
    return ({ user }) => {
      if (!user.isIdentified) return false
      return authZ(user, auth)
    }
  } else if (identified) {
    return ({ user }) => user.isIdentified
  } else if (hasAuthZ) {
    return ({ user }) => authZ(user, auth)
  }

  return undefined
}

////////////////////////////////////////////////////////////////////////////////
function normalizeChild({ parent, list }, tab) {
  tab = {
    ...tab,
    id: `${parent.id ? parent.id + '_' : ''}${tab.id}`
  }
  const [obj] = normalizeNavPoint([], tab, parent)
  return { parent, list: list.concat(freeze(obj)) }
}

////////////////////////////////////////////////////////////////////////////////
function normalizeRoute(
  {
    id,
    path,
    element,
    file,
    component: Component,
    lazy: Lazy,
    LAZY,
    index = false,
    loader,
    ...leftover
  },
  parent
) {
  if (Object.keys(leftover).length > 0) {
    console.warn('leftover', leftover)
    throw new Error(
      `Mis-configured point.route: leftover object keys when normalizing: ${id} ${path}`
    )
  }

  let base
  if (path) base = path.split('/')[0]

  id = id || parent.id
  const route = {
    id,
    path,
    base,
    index,
    ErrorBoundary,
    // HydrateFallback: Loading,
    loader
  }

  if (file) {
    route.file = file
  } else if (Lazy) {
    // not sure if this is really lazy loading or not
    // <Suspense fallback={<LoadingTop />}>
    route.element = <Lazy routeId={id} parentId={parent.id} />
    // </Suspense>
  } else if (LAZY) {
    const lazy = LAZY
    // route.lazy = () => import(lazy)
    route.file = lazy
  } else if (Component) {
    route.element = <Component routeId={id} parentId={parent.id} />
  } else if (element) {
    route.element = element
    // } else {
    //   if (parent.type !== TYPES.TAB)
    //     throw new Error('BUG: missing lazy or element in route spec')
  }

  return freeze(route)
}
